import { memo, useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSaveSimProfile } from "@/hooks/useSims";
import {
  PROVIDER_LABELS,
  countryName,
  type SimProfile,
  type SimProvider,
} from "@/lib/sims";

const NO_RATE_PLAN = "__none__";

type CountryRow = {
  key: number;
  countryCode: string;
  networks: string;
  note: string;
};

let nextKey = 0;
const countryRow = (
  countryCode = "",
  networks = "",
  note = "",
): CountryRow => ({
  key: nextKey++,
  countryCode,
  networks,
  note,
});

interface SimProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Profile to edit; null creates a new one. */
  profile: SimProfile | null;
  /** Rate plans seen on SIMs, per provider (to pick from). */
  ratePlans: Map<SimProvider, string[]>;
  /** Countries to prefill for a new profile. */
  defaultCountries: string[];
}

/** Create/edit a SIM profile: rate plan and preferred networks per country. */
export const SimProfileDialog = memo(function SimProfileDialog({
  open,
  onOpenChange,
  profile,
  ratePlans,
  defaultCountries,
}: SimProfileDialogProps) {
  const save = useSaveSimProfile();
  const [provider, setProvider] = useState<SimProvider>("webbing");
  const [name, setName] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [ratePlan, setRatePlan] = useState<string>(NO_RATE_PLAN);
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState<CountryRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setProvider(profile?.provider ?? "webbing");
    setName(profile?.name ?? "");
    setIsOpen(profile?.isOpen ?? false);
    setRatePlan(profile?.ratePlan ?? NO_RATE_PLAN);
    setNotes(profile?.notes ?? "");
    setRows(
      profile
        ? profile.networks.map((n) =>
            countryRow(n.countryCode, n.networks.join(", "), n.note ?? ""),
          )
        : defaultCountries.map((code) => countryRow(code)),
    );
  }, [open, profile, defaultCountries]);

  const planOptions = useMemo(() => {
    const plans = new Set(ratePlans.get(provider) ?? []);
    if (ratePlan !== NO_RATE_PLAN) plans.add(ratePlan);
    return Array.from(plans).sort();
  }, [ratePlans, provider, ratePlan]);

  const codes = rows.map((r) => r.countryCode.trim().toUpperCase());
  const invalidCode = codes.some((code) => !/^[A-Z]{2}$/.test(code));
  const duplicateCode = new Set(codes).size !== codes.length;
  const valid = name.trim() !== "" && !invalidCode && !duplicateCode;

  const updateRow = (key: number, patch: Partial<CountryRow>) =>
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );

  const submit = () => {
    save.mutate(
      {
        id: profile?.id,
        provider,
        name: name.trim(),
        isOpen,
        ratePlan: ratePlan === NO_RATE_PLAN ? null : ratePlan,
        notes: notes.trim() || null,
        networks: rows.map((r) => ({
          countryCode: r.countryCode.trim().toUpperCase(),
          networks: r.networks
            .split(",")
            .map((n) => n.trim())
            .filter(Boolean),
          note: r.note.trim() || null,
        })),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        data-testid="sim-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle>{profile ? "Edit profile" : "Add profile"}</DialogTitle>
          <DialogDescription>
            Which network the SIM prefers in each country. SIMs on the chosen
            rate plan get this profile automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select
                value={provider}
                disabled={!!profile}
                onValueChange={(value) => {
                  setProvider(value as SimProvider);
                  setRatePlan(NO_RATE_PLAN);
                }}
              >
                <SelectTrigger data-testid="sim-profile-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(PROVIDER_LABELS) as SimProvider[]).map((p) => (
                    <SelectItem key={p} value={p}>
                      {PROVIDER_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sim-profile-name">Name</Label>
              <Input
                id="sim-profile-name"
                data-testid="sim-profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Profile 1"
              />
            </div>
          </div>
          <div className="grid grid-cols-[1fr_auto] items-end gap-3">
            <div className="space-y-1.5">
              <Label>Rate plan</Label>
              <Select value={ratePlan} onValueChange={setRatePlan}>
                <SelectTrigger data-testid="sim-profile-rate-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_RATE_PLAN}>
                    None (assign SIMs by hand)
                  </SelectItem>
                  {planOptions.map((plan) => (
                    <SelectItem key={plan} value={plan}>
                      {plan}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex h-10 items-center gap-2 text-sm">
              <Switch
                checked={isOpen}
                onCheckedChange={setIsOpen}
                data-testid="sim-profile-open"
              />
              Open (any network)
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Preferred networks per country</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRows((prev) => [...prev, countryRow()])}
                data-testid="sim-profile-add-country"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Country
              </Button>
            </div>
            {rows.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No country preferences{isOpen ? " (open profile)" : ""}.
              </p>
            )}
            {rows.map((row, index) => {
              const code = row.countryCode.trim().toUpperCase();
              return (
                <div
                  key={row.key}
                  className="grid grid-cols-[4.5rem_1fr_1fr_auto] items-start gap-2"
                  data-testid={`sim-profile-country-${index}`}
                >
                  <div>
                    <Input
                      aria-label="Country code"
                      value={row.countryCode}
                      maxLength={2}
                      onChange={(e) =>
                        updateRow(row.key, { countryCode: e.target.value })
                      }
                      placeholder="CH"
                      className="uppercase"
                    />
                    <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {/^[A-Z]{2}$/.test(code) ? countryName(code) : "ISO code"}
                    </p>
                  </div>
                  <Input
                    aria-label="Networks"
                    value={row.networks}
                    onChange={(e) =>
                      updateRow(row.key, { networks: e.target.value })
                    }
                    placeholder="Sunrise, Salt (in order)"
                  />
                  <Input
                    aria-label="Note"
                    value={row.note}
                    onChange={(e) =>
                      updateRow(row.key, { note: e.target.value })
                    }
                    placeholder="Note"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove country"
                    onClick={() =>
                      setRows((prev) => prev.filter((r) => r.key !== row.key))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            {invalidCode && (
              <p className="text-xs text-destructive">
                Use two-letter country codes (e.g. CH, DE).
              </p>
            )}
            {duplicateCode && (
              <p className="text-xs text-destructive">
                Each country may only appear once.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sim-profile-notes">Notes</Label>
            <Textarea
              id="sim-profile-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="save-sim-profile"
            onClick={submit}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
